import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  City,
  ICandidate,
  QuarantineCompany,
  QuarantineUniversity,
  QuarantineCities,
} from "../../../Models";
import { useNotification } from "../../../context/NotificationContext";
import { QuarantineDataType, Sort } from "../Quarantine";
import { Spinner } from "../../Shared/Spinner";
import { ArrowRightIcon, LinkIcon } from "@heroicons/react/20/solid";
import { LinkedIn } from "@mui/icons-material";
import {
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";
import { Filters } from "../../../api/config";
import { getCityDetailsUsingGPT } from "../../../api/ai.api";
import {
  getCandidatesByCompanyUrl,
  getCandidatesByUniversityUrl,
  updateCityState,
  validateUniversityUrl,
  fixUniversityNames,
  FixUniversityNamesRequest,
  suggestUniversityName,
  fixCompanyNames,
  FixCompanyNamesRequest,
  suggestCompanyName,
} from "../../../api/quarantine.api";
import { deallocateSubtag, deallocateTag } from "../../../api/tag.api";

// ============================================================================
// TYPES & CONFIGURATION
// ============================================================================

const TABS = ["Companies", "Universities", "Cities"] as const;
type TabType = typeof TABS[number];

interface QuarantineProps {
  quarantineData?: any[];
  loading?: boolean;
  token: string;
  selected: string;
  setLoading: (loading: boolean) => void;
  setQuarantineData: (
    data: QuarantineUniversity[] | QuarantineCompany[]
  ) => void;
  fetchQuarantineData: (
    sortType?: string,
    sortDesc?: boolean,
    Filters?: Filters[]
  ) => Promise<void>;
  totalPages: number;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  selectedCompanyLocations: string[];
  setSelectedCompanyLocations: React.Dispatch<React.SetStateAction<string[]>>;
  sortOption: Sort | undefined;
  setSortOption: React.Dispatch<React.SetStateAction<Sort | undefined>>;
  selectedRows: QuarantineDataType[];
  setSelectedRows: React.Dispatch<React.SetStateAction<QuarantineDataType[]>>;
  toggleRow: (data: any, skipChildReset?: boolean) => void;
  showConfirmed: boolean;
  setShowRejectModalParent: React.Dispatch<React.SetStateAction<boolean>>;
  setSubCityClickPopUpParent: React.Dispatch<React.SetStateAction<boolean>>;
  setMajorCityClickPopUpParent: React.Dispatch<React.SetStateAction<boolean>>;
  getgptcitydetails: (cityId: string, details: any) => void;
  pageNumber: number;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  loadingMore?: boolean;
  urlValidationResults: Record<string, { isReachable: boolean; statusCode: number; message: string } | 'loading'>;
  setUrlValidationResults: React.Dispatch<React.SetStateAction<Record<string, { isReachable: boolean; statusCode: number; message: string } | 'loading'>>>;
}

interface CityDetails {
  action: string;
  corrected_name: string | null;
  reason: string;
  major_city: string;
  matched_city: string;
  matched_state: string;
  possible_states: string[];
}

// ============================================================================
// COLUMN CONFIGURATION - Define what columns to show per tab
// ============================================================================

interface ColumnConfig {
  showLinks: boolean;
  showCityName: boolean;
  showCityType: boolean;
  showGPTAction: boolean;
  showCount: boolean;
  showState: boolean;
  showNearestCity: boolean;
  showLocations: boolean;
  showName: boolean;
  showTags: boolean;
  expandable: boolean;
}

const COLUMN_CONFIGS: Record<TabType, ColumnConfig> = {
  Companies: {
    showLinks: true,
    showCityName: false,
    showCityType: false,
    showGPTAction: false,
    showCount: true,
    showState: false,
    showNearestCity: false,
    showLocations: true,
    showName: true,
    showTags: true,
    expandable: true,
  },
  Universities: {
    showLinks: true,
    showCityName: false,
    showCityType: false,
    showGPTAction: false,
    showCount: true,
    showState: false,
    showNearestCity: false,
    showLocations: true,
    showName: true,
    showTags: true,
    expandable: true,
  },
  Cities: {
    showLinks: false,
    showCityName: true,
    showCityType: true,
    showGPTAction: true,
    showCount: true,
    showState: true,
    showNearestCity: true,
    showLocations: false,
    showName: false,
    showTags: false,
    expandable: false,
  },
};

// ============================================================================
// REUSABLE CELL TEMPLATES
// ============================================================================

// Checkbox Cell
const CheckboxCell: React.FC<{
  data: any;
  selectedRows: any[];
  toggleRow: (data: any) => void;
  selectedCompanyLocations?: string[];
  selected?: string;
}> = ({ data, selectedRows, toggleRow, selectedCompanyLocations = [], selected }) => {
  const checkboxRef = React.useRef<HTMLInputElement>(null);
  const isSelected = selectedRows.some((row) => row.id === data.id);

  // Determine indeterminate state for parent rows with children
  let isIndeterminate = false;
  if (isSelected && data.urlGroups) {
    const allChildIds: string[] = [];
    if (selected === "Universities") {
      data.urlGroups.forEach((g: any) => {
        (g.universities ?? []).forEach((u: any) => allChildIds.push(u.id));
      });
    } else if (selected === "Companies") {
      data.urlGroups.forEach((g: any) => {
        (g.companyLocations ?? []).forEach((cl: any) => allChildIds.push(cl.id));
      });
    }
    if (allChildIds.length > 0) {
      const excludedCount = allChildIds.filter((id) => selectedCompanyLocations.includes(id)).length;
      isIndeterminate = excludedCount > 0 && excludedCount < allChildIds.length;
    }
  }

  React.useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  return (
    <td className="py-2 px-5">
      <input
        ref={checkboxRef}
        type="checkbox"
        className="rounded z-10 border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:border-gray-100"
        checked={isSelected}
        onChange={() => toggleRow(data)}
        onClick={(event) => event.stopPropagation()}
      />
    </td>
  );
};

// Links Cell (for Companies/Universities)
const LinksCell: React.FC<{ data: any }> = ({ data }) => (
  <td
    className={`px-4 py-1 font-semibold ${
      data.flag ? "text-red-500" : "text-gray-700 dark:text-white"
    }`}
  >
    <div className="flex gap-2">
      {data.linkedIn && (
        <a
          href={
            data.linkedIn?.startsWith("http")
              ? data.linkedIn
              : `https://${data.linkedIn}`
          }
          className="hover:text-blue-500"
          target="_blank"
          rel="noreferrer"
        >
          <LinkedIn width="1.3rem" />
        </a>
      )}
      <a
        onClick={(event) => event.stopPropagation()}
        href={
          data.url?.startsWith("http") ? data.url : `https://${data.url}`
        }
        className="hover:text-blue-500"
        target="_blank"
        rel="noreferrer"
      >
        <LinkIcon width="1.3rem" />
      </a>
    </div>
  </td>
);

// City Name Cell (for Cities tab)
const CityNameCell: React.FC<{
  data: any;
  index: number;
  openDialog: number | null;
  dialogLoading: boolean;
  currentCityDetails: CityDetails | undefined;
  showConfirmed: boolean;
  dialogRefs: React.MutableRefObject<Array<HTMLDivElement | null>>;
  toggleDialog: (index: number, data: City) => void;
  handleupdategpt: (index: number, data: City) => void;
  setOpenDialog: (index: number | null) => void;
  setShowRejectModalParent: (show: boolean) => void;
  setSubCityClickPopUpParent: (show: boolean) => void;
  setMajorCityClickPopUpParent: (show: boolean) => void;
}> = ({
  data,
  index,
  openDialog,
  dialogLoading,
  currentCityDetails,
  showConfirmed,
  dialogRefs,
  toggleDialog,
  handleupdategpt,
  setOpenDialog,
  setShowRejectModalParent,
  setSubCityClickPopUpParent,
  setMajorCityClickPopUpParent,
}) => (
  <td
    className={`px-4 py-1 font-semibold w-[20%] ${
      data.isNameChanged
        ? "dark:text-red-600 text-red-600"
        : "dark:text-gray-200"
    }`}
  >
    <div key={index} className="relative">
      <span className="flex flex-row items-center gap-2">
        {data.name}
        <ArrowTopRightOnSquareIcon
          height={16}
          className="text-blue-500 cursor-pointer toggle-icon"
          onClick={() => toggleDialog(index, data)}
        />
        <ArrowPathIcon
          height={16}
          className="text-green-500 cursor-pointer"
          onClick={() => handleupdategpt(index, data)}
        />
      </span>

      {/* Dialog Popup */}
      <div
        ref={(el) => {
          if (dialogRefs.current.length <= index) {
            dialogRefs.current = [
              ...dialogRefs.current,
              ...Array(index - dialogRefs.current.length + 1).fill(null),
            ];
          }
          dialogRefs.current[index] = el;
        }}
        className={`absolute left-full ml-2 top-0 bg-white shadow-lg rounded z-10
        transition-all duration-500 transform origin-left dark:bg-darkbglight dark:text-gray-400
        ${
          openDialog === index
            ? "opacity-100 scale-100"
            : "opacity-0 scale-95 pointer-events-none"
        }
        w-64 max-h-80 overflow-auto`}
      >
        <button
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
          onClick={() => setOpenDialog(null)}
        >
          <XMarkIcon height={16} />
        </button>

        <div className="pt-1">
          {dialogLoading ? (
            <div className="flex items-center justify-center h-24">
              <Spinner height="h-6" width="w-6" />
            </div>
          ) : (
            <div className="space-y-3 text-left cursor-default">
              {currentCityDetails && (
                <CityDetailsDialog
                  details={currentCityDetails}
                  showConfirmed={showConfirmed}
                  setShowRejectModalParent={setShowRejectModalParent}
                  setSubCityClickPopUpParent={setSubCityClickPopUpParent}
                  setMajorCityClickPopUpParent={setMajorCityClickPopUpParent}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  </td>
);

// City Details Dialog Component
const CityDetailsDialog: React.FC<{
  details: CityDetails;
  showConfirmed: boolean;
  setShowRejectModalParent: (show: boolean) => void;
  setSubCityClickPopUpParent: (show: boolean) => void;
  setMajorCityClickPopUpParent: (show: boolean) => void;
}> = ({
  details,
  showConfirmed,
  setShowRejectModalParent,
  setSubCityClickPopUpParent,
  setMajorCityClickPopUpParent,
}) => {
  const getActionColor = (action: string) => {
    const lowerAction = action.toLowerCase();
    if (lowerAction === "already exists" || lowerAction === "city exist with different name")
      return "text-blue-600 dark:text-blue-400";
    if (lowerAction === "valid city") return "text-green-600 dark:text-green-400";
    if (lowerAction === "invalid city") return "text-red-600 dark:text-red-400";
    if (lowerAction === "typo detected") return "text-amber-600 dark:text-amber-400";
    if (lowerAction === "state mismatch") return "text-purple-600 dark:text-purple-400";
    return "text-gray-600 dark:text-gray-400";
  };

  return (
    <div className="space-y-4 text-left cursor-default p-4 border rounded-lg shadow-sm">
      <div className="grid grid-cols-[auto_1fr] gap-x-2 items-center">
        <span className="font-medium">Action:</span>
        <span className={`font-semibold ${getActionColor(details.action)}`}>
          {details.action.toLowerCase() === "already exists" && showConfirmed
            ? "Multiple confirmed entries found"
            : details.action}
        </span>
      </div>

      {(details.action.toLowerCase() === "already exists" ||
        details.action.toLowerCase() === "city exist with different name") && (
        <>
          {details.matched_city && (
            <div className="grid grid-cols-[auto_1fr] gap-x-2">
              <span className="font-medium">Matched City:</span>
              <span className="dark:text-gray-300">{details.matched_city}</span>
            </div>
          )}
          {details.matched_state && (
            <div className="grid grid-cols-[auto_1fr] gap-x-2">
              <span className="font-medium">Matched State:</span>
              <span className="dark:text-gray-300">{details.matched_state}</span>
            </div>
          )}
          <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              Please{" "}
              <span className="font-semibold underline">Reject and Replace</span>{" "}
              the city with its associated state.
            </p>
          </div>
          <button
            className="py-2 px-3 rounded-md text-white bg-red-500"
            onClick={() => setShowRejectModalParent(true)}
          >
            Reject and Replace
          </button>
        </>
      )}

      {details.action.toLowerCase() !== "already exists" &&
        details.action.toLowerCase() !== "city exist with different name" && (
          <>
            {details.corrected_name && (
              <div className="grid grid-cols-[auto_1fr] gap-x-2">
                <span className="font-medium">Corrected Name:</span>
                <span className="dark:text-gray-300">
                  {details.action.toLowerCase() === "invalid city"
                    ? "UNKNOWN"
                    : details.corrected_name}
                </span>
              </div>
            )}

            {details.major_city !== undefined &&
              details.action.toLowerCase() !== "invalid city" && (
                <div className="grid grid-cols-[auto_1fr] gap-x-2">
                  <span className="font-medium">Major City:</span>
                  <span className="dark:text-gray-300">
                    {details.major_city &&
                    details.major_city !== null &&
                    details.major_city !== "null"
                      ? details.major_city
                      : "Itself a major city"}
                  </span>
                </div>
              )}

            {details.action.toLowerCase() === "valid city" && (
              <>
                <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                  <p className="text-sm text-green-800 dark:text-green-300">
                    {details.major_city &&
                    details.major_city !== null &&
                    details.major_city !== "null"
                      ? `New subcity under ${details.major_city}`
                      : "New major city"}
                  </p>
                </div>
                <div>
                  {details.major_city &&
                  details.major_city !== null &&
                  details.major_city !== "null" ? (
                    <button
                      className="py-2 px-3 rounded-md text-white bg-entntblue"
                      onClick={() => setSubCityClickPopUpParent(true)}
                    >
                      Confirm as Sub City
                    </button>
                  ) : (
                    <button
                      className="py-2 px-3 rounded-md text-white bg-entntblue"
                      onClick={() => setMajorCityClickPopUpParent(true)}
                    >
                      Confirm as Major City
                    </button>
                  )}
                </div>
              </>
            )}

            {details.reason && (
              <div className="space-y-1">
                <span className="font-medium">Reason:</span>
                <p className="text-sm text-gray-700 dark:text-gray-300 break-words whitespace-normal bg-gray-50 dark:bg-gray-800/50 p-2 rounded">
                  {details.reason}
                </p>
              </div>
            )}

            {details.possible_states && details.possible_states.length > 0 && (
              <div className="space-y-1">
                <span className="font-medium">Possible States:</span>
                <div className="flex flex-wrap gap-2">
                  {details.possible_states.map((state, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full text-sm"
                    >
                      {state}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
    </div>
  );
};

// City Type Cell
const CityTypeCell: React.FC<{ data: any; showConfirmed: boolean }> = ({
  data,
  showConfirmed,
}) =>
  showConfirmed ? (
    <td className="px-2 text-gray-700 dark:text-white font-semibold w-[5%]">
      <span>
        {data.nearestCity ? (
          <div className="rounded-md text-orange-300">Sub</div>
        ) : (
          <div className="rounded-md text-blue-500">Major</div>
        )}
      </span>
    </td>
  ) : null;

// GPT Action Cell (for Cities)
const GPTActionCell: React.FC<{
  data: any;
  showConfirmed: boolean;
  selectedRows: any[];
  toggleRow: (data: any) => void;
  setSelectedRows: (rows: any[]) => void;
  getgptcitydetails: (cityId: string, details: any) => void;
  setMajorCityClickPopUpParent: (show: boolean) => void;
  setSubCityClickPopUpParent: (show: boolean) => void;
  setShowRejectModalParent: (show: boolean) => void;
  loadingId: string | null;
  tickId: string | null;
  setLoadingId: (id: string | null) => void;
  setTickId: (id: string | null) => void;
  handleupdategpt: (index: number, data: any) => void;
  token: string;
}> = ({
  data,
  showConfirmed,
  selectedRows,
  toggleRow,
  setSelectedRows,
  getgptcitydetails,
  setMajorCityClickPopUpParent,
  setSubCityClickPopUpParent,
  setShowRejectModalParent,
  loadingId,
  tickId,
  setLoadingId,
  setTickId,
  handleupdategpt,
  token,
}) => (
  <td className="px-4 py-1 text-gray-700 dark:text-white font-semibold">
    <div className="flex items-center">
      <span
        className={`w-[240px] truncate font-semibold ${
          data.actiongpt?.action?.toLowerCase() === "already exists" ||
          data.actiongpt?.action?.toLowerCase() === "city exist with different name"
            ? "text-blue-600 dark:text-blue-400"
            : data.actiongpt?.action?.toLowerCase() === "valid city"
            ? "text-green-600 dark:text-green-400"
            : data.actiongpt?.action?.toLowerCase() === "invalid city"
            ? "text-red-600 dark:text-red-400"
            : data.actiongpt?.action?.toLowerCase() === "typo detected"
            ? "text-amber-600 dark:text-amber-400"
            : data.actiongpt?.action?.toLowerCase() === "state mismatch"
            ? "text-purple-600 dark:text-purple-400"
            : "text-gray-600 dark:text-gray-400"
        }`}
        title={data.actiongpt?.action}
      >
        {data.actiongpt?.action}
      </span>

      <div className="flex items-center">
        <div className="w-[150px]">
          <ActionButton
            data={data}
            showConfirmed={showConfirmed}
            selectedRows={selectedRows}
            toggleRow={toggleRow}
            setSelectedRows={setSelectedRows}
            getgptcitydetails={getgptcitydetails}
            setMajorCityClickPopUpParent={setMajorCityClickPopUpParent}
            setSubCityClickPopUpParent={setSubCityClickPopUpParent}
            setShowRejectModalParent={setShowRejectModalParent}
            loadingId={loadingId}
            tickId={tickId}
            setLoadingId={setLoadingId}
            setTickId={setTickId}
            handleupdategpt={handleupdategpt}
            token={token}
          />
        </div>

        {/* Info Icon with Tooltip */}
        <div className="cursor-pointer relative group ml-2">
          <svg
            className="w-5 h-5 text-gray-600 dark:text-gray-300 hover:text-blue-500 transition-colors"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 17C11.45 17 11 16.55 11 16V12C11 11.45 11.45 11 12 11C12.55 11 13 11.45 13 12V16C13 16.55 12.55 17 12 17ZM13 9H11V7H13V9Z"
              fill="currentColor"
            />
          </svg>

          <div className="absolute z-10 w-72 p-4 bg-gradient-to-br from-white to-blue-50 dark:from-gray-800 dark:to-gray-900 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -left-28 mt-3 text-sm border-2 border-blue-200 dark:border-blue-800 pointer-events-none transform hover:scale-105">
            <div className="font-extrabold text-xl text-blue-600 dark:text-blue-400 border-b-2 border-blue-200 dark:border-blue-700 pb-2 mb-3 flex items-center">
              City Information
            </div>
            <div className="grid grid-cols-3 gap-y-3 gap-x-2">
              {Object.entries(data.actiongpt || {})
                .filter(([key]) => key !== "action")
                .map(([key, value]) => (
                  <React.Fragment key={key}>
                    <span className="font-bold text-gray-700 dark:text-gray-300 col-span-1 capitalize">
                      {key.replace(/_/g, " ")}:
                    </span>
                    <span className="col-span-2 bg-blue-50 dark:bg-gray-700 px-2 py-1 rounded break-words text-xs flex items-center">
                      {value === "null" ? "-" : String(value)}
                    </span>
                  </React.Fragment>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </td>
);

// Action Button Component
const ActionButton: React.FC<{
  data: any;
  showConfirmed: boolean;
  selectedRows: any[];
  toggleRow: (data: any) => void;
  setSelectedRows: (rows: any[]) => void;
  getgptcitydetails: (cityId: string, details: any) => void;
  setMajorCityClickPopUpParent: (show: boolean) => void;
  setSubCityClickPopUpParent: (show: boolean) => void;
  setShowRejectModalParent: (show: boolean) => void;
  loadingId: string | null;
  tickId: string | null;
  setLoadingId: (id: string | null) => void;
  setTickId: (id: string | null) => void;
  handleupdategpt: (index: number, data: any) => void;
  token: string;
}> = ({
  data,
  showConfirmed,
  selectedRows,
  toggleRow,
  setSelectedRows,
  getgptcitydetails,
  setMajorCityClickPopUpParent,
  setSubCityClickPopUpParent,
  setShowRejectModalParent,
  loadingId,
  tickId,
  setLoadingId,
  setTickId,
  handleupdategpt,
  token,
}) => {
  const action = data.actiongpt?.action?.toLowerCase();

  const handleClick = (e: React.MouseEvent, callback: () => void) => {
    e.stopPropagation();
    if (!selectedRows.includes(data)) {
      toggleRow(data);
    }
    setSelectedRows([data]);
    callback();
  };

  if (action === "valid city" && !showConfirmed) {
    if (data.actiongpt.major_city === "null" || data.actiongpt.major_city === null) {
      return (
        <button
          className="py-2 px-3 rounded-md text-white bg-entntblue hover:bg-blue-600 transition-colors whitespace-nowrap"
          onClick={(e) =>
            handleClick(e, () => {
              getgptcitydetails(data.id, {
                corrected_name: data.name,
                correctedStateId: data.actiongpt?.correctedStateId,
              });
              setMajorCityClickPopUpParent(true);
            })
          }
        >
          Confirm as MajorCity
        </button>
      );
    }
    return (
      <button
        className="w-full h-auto px-3 py-1 rounded-md text-white bg-entntblue text-sm hover:bg-blue-600 transition-colors whitespace-normal break-words"
        onClick={(e) =>
          handleClick(e, () => {
            getgptcitydetails(data.id, {
              major_city: data.actiongpt.major_city,
              correctedStateId: data.actiongpt?.correctedStateId,
            });
            setSubCityClickPopUpParent(true);
          })
        }
      >
        Confirm {data.name} as Sub City of {data.actiongpt.major_city}
      </button>
    );
  }

  if (action === "typo detected") {
    if (data.actiongpt.major_city === "null") {
      return (
        <button
          className="py-2 px-3 rounded-md text-white bg-entntblue hover:bg-blue-600 transition-colors whitespace-nowrap"
          onClick={(e) =>
            handleClick(e, () => {
              getgptcitydetails(data.id, {
                corrected_name: data.actiongpt.corrected_name,
                correctedStateId: data.actiongpt?.correctedStateId,
              });
              setMajorCityClickPopUpParent(true);
            })
          }
        >
          Confirm as MajorCity
        </button>
      );
    }
    return (
      <button
        className="w-full h-auto px-3 py-1 rounded-md text-white bg-entntblue text-sm hover:bg-blue-600 transition-colors whitespace-normal break-words"
        onClick={(e) =>
          handleClick(e, () => {
            getgptcitydetails(data.id, {
              corrected_name: data.actiongpt.corrected_name,
              major_city: data.actiongpt.major_city,
              correctedStateId: data.actiongpt?.correctedStateId,
            });
            setSubCityClickPopUpParent(true);
          })
        }
      >
        Confirm {data.actiongpt.corrected_name} as Sub City of{" "}
        {data.actiongpt.major_city}
      </button>
    );
  }

  if (action === "already exists" || action === "city exist with different name") {
    return (
      <button
        className="w-full h-auto px-3 py-1 rounded-md text-white bg-red-500 text-sm hover:bg-red-600 transition-colors whitespace-normal break-words"
        onClick={(e) =>
          handleClick(e, () => {
            getgptcitydetails(data.id, { corrected_name: data.actiongpt.matched_city });
            setShowRejectModalParent(true);
          })
        }
      >
        Reject and Replace {data.name} with {data.actiongpt.matched_city}
      </button>
    );
  }

  if (action === "invalid city") {
    return (
      <button
        className="w-full h-auto px-3 py-1 rounded-md text-white bg-red-500 text-sm hover:bg-red-600 transition-colors whitespace-normal break-words"
        onClick={(e) =>
          handleClick(e, () => {
            getgptcitydetails(data.id, { corrected_name: "Invalid" });
            setShowRejectModalParent(true);
          })
        }
      >
        Reject {data.name}
      </button>
    );
  }

  if (action === "state mismatch") {
    return (
      <button
        className="w-full h-auto px-3 py-1 rounded-md text-white bg-purple-800 text-sm hover:bg-purple-400 transition-colors whitespace-normal break-words flex items-center justify-center gap-2"
        onClick={async (e) => {
          e.stopPropagation();
          if (!selectedRows.includes(data)) {
            toggleRow(data);
          }
          setSelectedRows([data]);

          const gptState = data.actiongpt.possible_states[0];
          const allCityIds = [
            data.id,
            ...(data.replacedCities?.map((c: { id: string }) => c.id) || []),
          ];

          setLoadingId(data.id);
          setTickId(null);

          try {
            await Promise.all(
              allCityIds.map((id) => updateCityState(id, gptState, token))
            );
            await handleupdategpt(0, data);
            setTickId(data.id);
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } catch (err) {
            console.error(err);
            alert("Failed to update some cities");
          } finally {
            setLoadingId(null);
          }
        }}
      >
        {loadingId === data.id ? (
          <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4"></span>
        ) : tickId === data.id ? (
          <span>✅</span>
        ) : (
          <>Correct the state to {data.actiongpt.possible_states[0]}</>
        )}
      </button>
    );
  }

  return null;
};

// Count Cell
const CountCell: React.FC<{ data: any; isCity: boolean }> = ({ data, isCity }) =>
  isCity ? (
    <td className="px-4 py-1 text-gray-700 dark:text-white font-semibold">
      <span className="bg-entntblue text-white rounded-md py-0.5 px-2">
        {data.count}
      </span>
    </td>
  ) : (
    <td className="px-4 py-1 text-gray-700 dark:text-white font-semibold">
      {data.count}
    </td>
  );

// State Cell
const StateCell: React.FC<{ data: any }> = ({ data }) => {
  const currentStateId = data?.state?.id;
  const suggestedStateId =
    data?.actiongpt?.correctedStateId ?? data?.actiongpt?.corrected_state_id;

  const hasStateWarning =
    !!currentStateId &&
    !!suggestedStateId &&
    currentStateId !== suggestedStateId;
  

  return (
    <td className="px-4 py-1 text-gray-700 dark:text-white font-semibold">
      <span className="inline-flex items-center gap-1">
        {data?.state?.name}
        {hasStateWarning && (
          <ExclamationTriangleIcon
            className="w-4 h-4 text-yellow-500"
            title="Suggested state differs from current state"
          />
        )}
      </span>
    </td>
  );
};

// Nearest City Cell
const NearestCityCell: React.FC<{ data: any }> = ({ data }) => (
  <td className="px-4 py-1 text-gray-700 dark:text-white font-semibold">
    {data.nearestCity ?? "-"}
  </td>
);

// Locations Cell (for Companies/Universities)
const LocationsCell: React.FC<{
  data: any;
  selected: string;
  selectedCompanyLocations: string[];
  toggleSelectedOptions: (id: string) => void;
}> = ({ data, selected, selectedCompanyLocations, toggleSelectedOptions }) => (
  <td className="px-4 py-1">
    <div className="relative flex flex-row justify-start flex-wrap max-w-[22rem] overflow-x-hidden overflow-y-auto gap-1 text-gray-700 dark:text-white font-semibold">
      {selected === "Companies" &&
        data.companyLocations?.map((cl: any) => {
          const relatedVariantLocations = data.urlGroups && data.urlGroups.length > 0
            ? data.urlGroups.flatMap((g: any) => g.companyLocations ?? []).filter((c: any) => c.city?.id === cl.city?.id)
            : [];

          const isSelected = relatedVariantLocations.length > 0
            ? relatedVariantLocations.every((c: any) => selectedCompanyLocations.includes(c.id))
            : selectedCompanyLocations.includes(cl.id);

          return (
            <LocationBadge
              key={cl.id}
              location={cl}
              isSelected={isSelected}
              onClick={(e) => {
                e.stopPropagation();
                
                const allSelectedExceptThis = data.companyLocations.every(
                  (location: any) => {
                    const otherRelated = data.urlGroups && data.urlGroups.length > 0
                      ? data.urlGroups.flatMap((g: any) => g.companyLocations ?? []).filter((c: any) => c.city?.id === location.city?.id)
                      : [];
                    const isOtherSelected = otherRelated.length > 0
                      ? otherRelated.every((c: any) => selectedCompanyLocations.includes(c.id))
                      : selectedCompanyLocations.includes(location.id);
                    return location.city?.id === cl.city?.id || isOtherSelected;
                  }
                );

                if (!allSelectedExceptThis) {
                  if (relatedVariantLocations.length > 0) {
                    const shouldExclude = !isSelected;
                    relatedVariantLocations.forEach((c: any) => {
                      const currentlyExcluded = selectedCompanyLocations.includes(c.id);
                      if (currentlyExcluded !== shouldExclude) {
                        toggleSelectedOptions(c.id);
                      }
                    });
                  } else {
                    toggleSelectedOptions(cl.id);
                  }
                }
              }}
            />
          );
        })}

      {selected === "Universities" &&
        (() => {
          const allUnis = data.urlGroups?.flatMap((g: any) => g.universities || []) ?? [];
          const cityMap = new Map<string, any>();
          allUnis.forEach((uni: any) => {
            if (!uni.city?.name) return;
            const key = uni.city.name.toLowerCase();
            if (cityMap.has(key)) {
              cityMap.get(key)!.count += uni.count;
            } else {
              cityMap.set(key, { id: uni.city.id, name: uni.city.name, isConfirmed: uni.city.isConfirmed, majorCity: uni.city.majorCity, count: uni.count });
            }
          });
          return Array.from(cityMap.values()).map((city: any) => (
            <LocationBadge
              key={city.id}
              location={city}
              isSelected={(() => {
                const cityUnis = allUnis.filter((u: any) => u.city?.id === city.id);
                return cityUnis.length > 0 
                  ? cityUnis.every((u: any) => selectedCompanyLocations.includes(u.id))
                  : selectedCompanyLocations.includes(city.id);
              })()}
              onClick={(e) => {
                e.stopPropagation();
                const cities = Array.from(cityMap.values());
                const cityUnis = allUnis.filter((u: any) => u.city?.id === city.id);
                
                const isCurrentlySelected = cityUnis.length > 0 
                  ? cityUnis.every((u: any) => selectedCompanyLocations.includes(u.id))
                  : selectedCompanyLocations.includes(city.id);

                const allSelectedExceptThis = cities.every(
                  (location: any) => {
                    const locUnis = allUnis.filter((u: any) => u.city?.id === location.id);
                    const isLocSelected = locUnis.length > 0
                      ? locUnis.every((u: any) => selectedCompanyLocations.includes(u.id))
                      : selectedCompanyLocations.includes(location.id);
                    return location.id === city.id || isLocSelected;
                  }
                );

                if (!allSelectedExceptThis) {
                  if (cityUnis.length > 0) {
                    const shouldExclude = !isCurrentlySelected;
                    cityUnis.forEach((u: any) => {
                      const currentlyExcluded = selectedCompanyLocations.includes(u.id);
                      if (currentlyExcluded !== shouldExclude) {
                        toggleSelectedOptions(u.id);
                      }
                    });
                  } else {
                    toggleSelectedOptions(city.id);
                  }
                }
              }}
            />
          ));
        })()}
    </div>
  </td>
);

// Location Badge Component
const LocationBadge: React.FC<{
  location: any;
  isSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
}> = ({ location, isSelected, onClick }) => {
  const city = location.city || location;
  
  return (
    <span
      className={`${
        isSelected
          ? "border border-black bg-transparent"
          : city.isConfirmed
          ? "bg-red-500 text-white"
          : "bg-entntblue text-white"
      } cursor-pointer rounded-md py-0.5 px-1 m-1`}
      onClick={onClick}
      title={city.count}
    >
      <span>
        {city.isConfirmed ? (
          <span className="pr-1">
            {city?.majorCity ? (
              <span title={`Sub City Of ${city?.majorCity.name}`}>
                {city?.name}
              </span>
            ) : (
              <span title={`${city.name} is a major city`}>{city.name}</span>
            )}
          </span>
        ) : (
          <span className="pr-1">{city?.name}</span>
        )}
        {city?.name && (
          <span className="text-white border-l border-white px-1 w-full">
            {city?.count}
          </span>
        )}
      </span>
    </span>
  );
};

// Name Cell
const NameCell: React.FC<{ data: any }> = ({ data }) => (
  <td className="px-4 py-1 text-gray-700 dark:text-white font-semibold">
    {data.name}
  </td>
);

// Tags Cell
const TagsCell: React.FC<{
  data: any;
  handleRemoveTag: (tagId: string, entityId: string, isSubtag: boolean) => void;
}> = ({ data, handleRemoveTag }) => (
  <td className="px-2 py-1 text-gray-700 dark:text-white max-w-[300px]">
    <div className="flex flex-wrap gap-3">
      {data.tags &&
        data.tags.length > 0 &&
        data.tags.map((tag: any, index: number) => (
          <span
            className="rounded-xl border-2 bg-blue-100 text-entntblue px-2 flex gap-3 items-center dark:bg-entntblue dark:text-blue-100 font-medium"
            key={tag.id + index}
          >
            {tag.name}
            <XMarkIcon
              onClick={(event) => {
                event.stopPropagation();
                handleRemoveTag(tag.id, data.id, false);
              }}
              className="h-4 w-4 cursor-pointer text-gray-500 ml-1 hover:scale-110 hover:text-red-300"
            />
          </span>
        ))}

      {data.subtags &&
        data.subtags.length > 0 &&
        data.subtags.map((subtag: any, index: number) => (
          <div
            className="rounded-xl border-2 bg-blue-100 flex text-entntblue px-2 gap-3 items-center dark:bg-entntblue dark:text-blue-100 font-medium min-w-[200px]"
            key={subtag.id + index}
          >
            {subtag.tagName}
            <ArrowRightIcon className="h-3 w-3" />
            {subtag.name}
            <XMarkIcon
              onClick={(event) => {
                event.stopPropagation();
                handleRemoveTag(subtag.tagSubtagId, data.id, true);
              }}
              className="h-4 w-4 cursor-pointer text-gray-500 ml-1 hover:scale-110 hover:text-red-300"
            />
          </div>
        ))}
    </div>
  </td>
);

// Expanded Row Component
const ExpandedRow: React.FC<{
  data: any;
  selected: string;
  students: ICandidate[];
}> = ({ data, selected, students }) => (
  <tr className="border-b border-gray-200">
    <td colSpan={6} className="p-3">
      <div className="overflow-hidden px-6">
        <div className="flex flex-col items-start gap-y-4">
          {selected === "Universities" && (
            <div className="w-full">
              <p className="font-bold">Degrees:</p>
              <p>{data.degrees}</p>
            </div>
          )}
          {students.length > 0 && (
            <div className="w-full mt-4">
              <p className="font-bold">Candidates:</p>
              <div className="max-h-[20rem] overflow-y-scroll grid grid-cols-1 gap-2 mt-2">
                {students.map((student: ICandidate) => (
                  <div
                    key={student.id}
                    className="p-2 bg-gray-100 dark:bg-gray-700 rounded-md shadow-md"
                  >
                    <a
                      href={`/candidates/${student.id}`}
                      className="hover:underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {student.firstName} {student.lastName}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </td>
  </tr>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const DataGrid: React.FC<QuarantineProps> = ({
  quarantineData,
  fetchQuarantineData,
  loading,
  setLoading,
  setQuarantineData,
  selected,
  token,
  selectedRows,
  setSelectedRows,
  selectedCompanyLocations,
  setSelectedCompanyLocations,
  toggleRow,
  showConfirmed,
  setShowRejectModalParent,
  setSubCityClickPopUpParent,
  setMajorCityClickPopUpParent,
  getgptcitydetails,
  pageNumber,
  setPageNumber,
  loadingMore,
  urlValidationResults,
  setUrlValidationResults,
}) => {
  const { success, error: notifyError } = useNotification();
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // Drag-and-drop merge state (Universities & Companies)
  const [dragOverRowId, setDragOverRowId] = useState<string | null>(null);
  const [mergeDialog, setMergeDialog] = useState<{
    sourceData: any;
    targetData: any;
    sourceUrlGroup?: any; // set when dragging a child URL group
    chosenName: string;
    customName: string;
    nameChoice: 'source' | 'target' | 'custom';
  } | null>(null);
  const [isMerging, setIsMerging] = useState(false);
  const [isAiSuggesting, setIsAiSuggesting] = useState(false);
  const dragRef = useRef<{ type: 'parent' | 'child'; data: any; urlGroup?: any } | null>(null);
  const [students, setStudents] = useState<ICandidate[]>([]);
  const [candidatesModalOpen, setCandidatesModalOpen] = useState(false);
  const [candidatesModalUrl, setCandidatesModalUrl] = useState<string | null>(null);
  const [candidatesModalStudents, setCandidatesModalStudents] = useState<ICandidate[]>([]);
  const [candidatesModalLoading, setCandidatesModalLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState<number | null>(null);
  const dialogRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [currentCityDetails, setCurrentCityDetails] = useState<CityDetails | undefined>();
  const [dialogLoading, setDialogLoading] = useState<boolean>(false);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [tickId, setTickId] = useState<string | null>(null);

  // Get current column configuration
  const columnConfig = COLUMN_CONFIGS[selected as TabType];

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const toggleDialog = async (index: number, data: City) => {
    setSelectedRows([data as QuarantineUniversity]);
    if (openDialog === index) {
      setCurrentCityDetails(undefined);
      setOpenDialog(null);
    } else {
      setOpenDialog(index);
      setDialogLoading(true);
      const res = await getCityDetailsUsingGPT(data, token);
      setCurrentCityDetails(res);
      setDialogLoading(false);
    }
  };

  const handleupdategpt = async (index: number, data: City) => {
    await getCityDetailsUsingGPT(data, token).then(() => {
      success("Updation Successful", `${selected} Updated Successfully.`);
      setPageNumber(1);
      setQuarantineData([]);
      setSelectedRows([]);
      fetchQuarantineData();
    });
  };

  const toggleRowExpand = async (index: any, data: any) => {
    if (expandedRow === index) {
      setExpandedRow(null);
      setStudents([]);
    } else {
      setExpandedRow(index);

      try {
        let candidates: ICandidate[] = [];

        // For companies tab, fetch candidates by URL (only for single-URL companies)
        if (selected === TABS[0] && (!data.urlGroups || data.urlGroups.length <= 1)) {
          const furl = (data.url || "").replace(/^(https?:\/\/)?(www\.)?/, "").replace(/\/$/, "");
          candidates = await getCandidatesByCompanyUrl(furl, token);
        }

        // Auto-validate all URLs when university or company row expands
        if ((selected === TABS[1] || selected === TABS[0]) && data.urlGroups) {
          for (const group of data.urlGroups) {
            if (group.url && !urlValidationResults[group.url]) {
              setUrlValidationResults((prev) => ({ ...prev, [group.url]: 'loading' }));
              validateUniversityUrl(group.url, token)
                .then((result) => setUrlValidationResults((prev) => ({ ...prev, [group.url]: result })))
                .catch(() => setUrlValidationResults((prev) => ({ ...prev, [group.url]: { isReachable: false, statusCode: 0, message: 'Error' } })));
            }
          }
        }

        setStudents(candidates);
      } catch (error) {
        console.error("Error fetching candidates:", error);
      }
    }
  };

  const handleRemoveTag = async (
    tagId: string,
    entityId: string,
    isSubtag: boolean
  ) => {
    try {
      const deallocate = isSubtag ? deallocateSubtag : deallocateTag;
      await deallocate(
        entityId,
        tagId,
        selected === TABS[0],
        "quarantine",
        token
      );
      setPageNumber(1);
      setQuarantineData([]);
      fetchQuarantineData(undefined, undefined);
    } catch (ex) {
      console.log(ex);
    }
  };

  const toggleSelectedOptions = (newId: string) => {
    setSelectedCompanyLocations((prev) => {
      const isAlready = prev.some((id) => id === newId);
      if (isAlready) {
        return prev.filter((id) => id !== newId);
      } else {
        return [...prev, newId];
      }
    });
  };

  // --- Drag-and-drop merge handlers (Universities & Companies) ---
  const handleRowDragStart = (data: any) => {
    dragRef.current = { type: 'parent', data };
  };

  const handleUrlGroupDragStart = (parentData: any, urlGroup: any) => {
    dragRef.current = { type: 'child', data: parentData, urlGroup };
  };

  const handleRowDragOver = (e: React.DragEvent, rowId: string) => {
    e.preventDefault();
    if (dragRef.current && dragRef.current.data.id !== rowId) {
      setDragOverRowId(rowId);
    }
  };

  const handleRowDragLeave = () => {
    setDragOverRowId(null);
  };

  const handleRowDrop = (e: React.DragEvent, targetData: any) => {
    e.preventDefault();
    setDragOverRowId(null);
    if (!dragRef.current || dragRef.current.data.id === targetData.id) return;

    const { type, data: sourceData, urlGroup } = dragRef.current;
    dragRef.current = null;

    setMergeDialog({
      sourceData,
      targetData,
      sourceUrlGroup: type === 'child' ? urlGroup : undefined,
      chosenName: targetData.name,
      customName: '',
      nameChoice: 'target',
    });
  };

  const handleAiSuggestName = async () => {
    if (!mergeDialog) return;
    setIsAiSuggesting(true);
    try {
      const { sourceData, targetData } = mergeDialog;
      const names = [sourceData.name, targetData.name];
      const urls = [
        (sourceData.urlGroups?.map((g: any) => g.url).filter(Boolean) ?? []),
        (targetData.urlGroups?.map((g: any) => g.url).filter(Boolean) ?? []),
      ];

      let suggested = "";
      if (selected === TABS[1]) {
        // Universities
        const cities = [
          (sourceData.urlGroups?.flatMap((g: any) => g.universities.map((u: any) => u.city?.name).filter(Boolean)) ?? []),
          (targetData.urlGroups?.flatMap((g: any) => g.universities.map((u: any) => u.city?.name).filter(Boolean)) ?? []),
        ];
        suggested = await suggestUniversityName(names, cities, urls, token);
      } else if (selected === TABS[0]) {
        // Companies
        const locations = [
          (sourceData.urlGroups?.flatMap((g: any) => g.companyLocations.map((cl: any) => cl.city?.name).filter(Boolean)) ?? []),
          (targetData.urlGroups?.flatMap((g: any) => g.companyLocations.map((cl: any) => cl.city?.name).filter(Boolean)) ?? []),
        ];
        suggested = await suggestCompanyName(names, urls, locations, token);
      }

      setMergeDialog(prev => prev ? { ...prev, nameChoice: 'custom', customName: suggested } : null);
    } catch {
      notifyError("AI Suggest Failed", "Could not get AI suggestion.");
    } finally {
      setIsAiSuggesting(false);
    }
  };

  const handleMergeConfirm = async () => {
    if (!mergeDialog) return;
    const { sourceData, targetData, sourceUrlGroup, nameChoice, customName } = mergeDialog;
    const chosenName = nameChoice === 'source' ? sourceData.name : nameChoice === 'target' ? targetData.name : customName.trim();
    if (!chosenName) return;

    setIsMerging(true);
    try {
      if (selected === TABS[1]) {
        // Universities
        let universityIds: string[] = [];
        if (sourceUrlGroup) {
          universityIds = sourceUrlGroup.universities.map((u: any) => u.id);
        } else {
          universityIds = sourceData.urlGroups?.flatMap((g: any) => g.universities.map((u: any) => u.id)) ?? [];
        }
        if (chosenName !== targetData.name) {
          const targetIds = targetData.urlGroups?.flatMap((g: any) => g.universities.map((u: any) => u.id)) ?? [];
          universityIds = [...universityIds, ...targetIds];
        }

        const payload: FixUniversityNamesRequest[] = [{
          correctName: chosenName,
          universityIds,
        }];
        await fixUniversityNames(payload, token);
      } else if (selected === TABS[0]) {
        // Companies
        let companyIds: string[] = [];
        if (sourceUrlGroup) {
          companyIds = sourceUrlGroup.companyLocations.map((cl: any) => cl.companyId || sourceData.id || sourceData.companyId);
        } else {
          companyIds = sourceData.urlGroups?.flatMap((g: any) => g.companyLocations.map((cl: any) => cl.companyId || sourceData.id || sourceData.companyId)) ?? [];
        }
        if (chosenName !== targetData.name) {
          const targetIds = targetData.urlGroups?.flatMap((g: any) => g.companyLocations.map((cl: any) => cl.companyId || targetData.id || targetData.companyId)) ?? [];
          companyIds = [...companyIds, ...targetIds];
        }

        const payload: FixCompanyNamesRequest[] = [{
          correctName: chosenName,
          companyIds,
        }];
        await fixCompanyNames(payload, token);
      }

      // Update local state
      const currentData = [...(quarantineData || [])];
      const sourceIdx = currentData.findIndex((d: any) => d.id === sourceData.id);
      const targetIdx = currentData.findIndex((d: any) => d.id === targetData.id);
      if (sourceIdx !== -1 && targetIdx !== -1) {
        const target = { ...currentData[targetIdx] };
        target.name = chosenName;

        if (sourceUrlGroup) {
          // Move only the dragged URL group to target
          target.urlGroups = [...(target.urlGroups || []), sourceUrlGroup];
          target.count = (target.count || 0) + (sourceUrlGroup.count || 0);

          // Remove from source
          const source = { ...currentData[sourceIdx] };
          source.urlGroups = (source.urlGroups || []).filter((g: any) => g.url !== sourceUrlGroup.url);
          source.count = (source.count || 0) - (sourceUrlGroup.count || 0);

          if (source.urlGroups.length === 0) {
            currentData.splice(sourceIdx, 1);
            const newTargetIdx = currentData.findIndex((d: any) => d.id === targetData.id);
            if (newTargetIdx !== -1) currentData[newTargetIdx] = target;
          } else {
            currentData[sourceIdx] = source;
            currentData[targetIdx] = target;
          }
        } else {
          // Merge all URL groups from source into target
          target.urlGroups = [...(target.urlGroups || []), ...(sourceData.urlGroups || [])];
          target.count = (target.count || 0) + (sourceData.count || 0);
          // Combine tags
          const existingTagIds = new Set((target.tags || []).map((t: any) => t.id));
          const newTags = (sourceData.tags || []).filter((t: any) => !existingTagIds.has(t.id));
          target.tags = [...(target.tags || []), ...newTags];
          const existingSubtagIds = new Set((target.subtags || []).map((t: any) => t.tagSubtagId));
          const newSubtags = (sourceData.subtags || []).filter((t: any) => !existingSubtagIds.has(t.tagSubtagId));
          target.subtags = [...(target.subtags || []), ...newSubtags];

          currentData[targetIdx] = target;
          currentData.splice(sourceIdx, 1);
        }

        setQuarantineData(currentData as any);
      }

      success("Merged", sourceUrlGroup
        ? `Moved URL group "${sourceUrlGroup.url}" to "${chosenName}"`
        : `Merged "${sourceData.name}" into "${chosenName}"`
      );
      setExpandedRow(null);
      setPageNumber(1);
      setQuarantineData([]);
      await fetchQuarantineData();
    } catch (err: any) {
      notifyError("Merge Failed", err.message);
    } finally {
      setIsMerging(false);
      setMergeDialog(null);
    }
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDialog !== null) {
        const currentDialogRef = dialogRefs.current[openDialog];

        if (
          currentDialogRef &&
          !currentDialogRef.contains(event.target as Node)
        ) {
          const isToggleIcon = (event.target as Element).closest(".toggle-icon");
          if (!isToggleIcon) {
            setOpenDialog(null);
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDialog]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading === true) {
    return (
      <tbody>
        <tr className="h-80">
          <td colSpan={9}>
            <Spinner height="h-10" width="w-10" />
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <>
    <tbody>
      {quarantineData!.map((data, index) => (
        <React.Fragment key={data.id}>
          <tr
            onClick={() => {
              if (columnConfig.expandable) {
                toggleRowExpand(index, data);
              }
            }}
            draggable={selected === TABS[1] || selected === TABS[0]}
            onDragStart={(e) => {
              if (selected === TABS[1] || selected === TABS[0]) {
                handleRowDragStart(data);
                e.dataTransfer.effectAllowed = 'move';
              }
            }}
            onDragOver={(e) => {
              if (selected === TABS[1] || selected === TABS[0]) handleRowDragOver(e, data.id);
            }}
            onDragLeave={() => {
              if (selected === TABS[1] || selected === TABS[0]) handleRowDragLeave();
            }}
            onDrop={(e) => {
              if (selected === TABS[1] || selected === TABS[0]) handleRowDrop(e, data);
            }}
            className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1c2229] border-t border-gray-200 ${
              selected === TABS[1] ? 'cursor-grab active:cursor-grabbing' : ''
            } ${dragOverRowId === data.id ? 'bg-blue-50 dark:bg-blue-900/20 ring-2 ring-inset ring-entntblue' : ''}`}
          >
            {/* Checkbox - Always visible */}
            <CheckboxCell
              data={data}
              selectedRows={selectedRows}
              toggleRow={toggleRow}
              selectedCompanyLocations={selectedCompanyLocations}
              selected={selected}
            />

            {/* Links Cell - no longer shown for Companies in parent row (URLs are in expanded children now) */}
            {columnConfig.showLinks && selected !== TABS[0] && selected !== TABS[1] && <LinksCell data={data} />}

            {/* URL Groups Badge for Universities and Companies */}
            {(selected === TABS[1] || selected === TABS[0]) && data.urlGroups && (
              <td className="px-2 py-1">
                <span className="inline-flex items-center rounded-md text-white text-xs font-medium px-2 py-0.5 bg-entntblue">
                  {data.urlGroups.length} URL{data.urlGroups.length !== 1 ? 's' : ''}
                </span>
              </td>
            )}

            {/* City Name Cell */}
            {columnConfig.showCityName && (
              <CityNameCell
                data={data}
                index={index}
                openDialog={openDialog}
                dialogLoading={dialogLoading}
                currentCityDetails={currentCityDetails}
                showConfirmed={showConfirmed}
                dialogRefs={dialogRefs}
                toggleDialog={toggleDialog}
                handleupdategpt={handleupdategpt}
                setOpenDialog={setOpenDialog}
                setShowRejectModalParent={setShowRejectModalParent}
                setSubCityClickPopUpParent={setSubCityClickPopUpParent}
                setMajorCityClickPopUpParent={setMajorCityClickPopUpParent}
              />
            )}

            {/* City Type Cell */}
            {columnConfig.showCityType && (
              <CityTypeCell data={data} showConfirmed={showConfirmed} />
            )}

            {/* GPT Action Cell */}
            {columnConfig.showGPTAction && (
              <GPTActionCell
                data={data}
                showConfirmed={showConfirmed}
                selectedRows={selectedRows}
                toggleRow={toggleRow}
                setSelectedRows={setSelectedRows}
                getgptcitydetails={getgptcitydetails}
                setMajorCityClickPopUpParent={setMajorCityClickPopUpParent}
                setSubCityClickPopUpParent={setSubCityClickPopUpParent}
                setShowRejectModalParent={setShowRejectModalParent}
                loadingId={loadingId}
                tickId={tickId}
                setLoadingId={setLoadingId}
                setTickId={setTickId}
                handleupdategpt={handleupdategpt}
                token={token}
              />
            )}

            {/* Count Cell */}
            {columnConfig.showCount && (
              <CountCell data={data} isCity={selected === TABS[2]} />
            )}

            {/* State Cell */}
            {columnConfig.showState && <StateCell data={data} />}

            {/* Nearest City Cell */}
            {columnConfig.showNearestCity && <NearestCityCell data={data} />}

            {/* Locations Cell */}
            {columnConfig.showLocations && (
              <LocationsCell
                data={data}
                selected={selected}
                selectedCompanyLocations={selectedCompanyLocations}
                toggleSelectedOptions={toggleSelectedOptions}
              />
            )}

            {/* Name Cell */}
            {columnConfig.showName && <NameCell data={data} />}

            {/* Tags Cell */}
            {columnConfig.showTags && (
              <TagsCell data={data} handleRemoveTag={handleRemoveTag} />
            )}
          </tr>

          {/* Expanded Row */}
          {columnConfig.expandable && expandedRow === index && (
            <tr className="border-b border-gray-200">
              <td colSpan={6} className="p-3">
                <div className="overflow-hidden px-6">
                  <div className="flex flex-col items-start gap-y-4">
                    {selected === TABS[1] && (
                      <div className="w-full">
                        <p className="font-bold">Degrees:</p>
                        <p>{data.degrees}</p>
                      </div>
                    )}

                    {/* URL Groups for Companies */}
                    {selected === TABS[0] && data.urlGroups && (
                      <div className="w-full">
                        <p className="font-bold mb-2">URL Variants:</p>
                        <div className="flex flex-col gap-2">
                           {(() => {
                             const getCandidates = (g: any) => (g.companyLocations ?? []).reduce((sum: number, cl: any) => sum + (cl.experienceCount ?? 0), 0);
                             const isClean = (url: string) => {
                               if (!url) return false;
                               const domainPart = url.replace(/^(https?:\/\/)?(www\.)?/, "").split("/")[0].split("?")[0].split("#")[0];
                               return url.replace(/^(https?:\/\/)?(www\.)?/, "").trim() === domainPart.trim();
                             };
                             const isValid = (url: string) => {
                               const res = urlValidationResults[url];
                               return res && res !== 'loading' ? (res as any).isReachable : true; // Assume true if not yet checked/loading
                             };

                             const sortedGroups = [...data.urlGroups].sort((a, b) => getCandidates(b) - getCandidates(a));
                             const validAndClean = sortedGroups.filter(g => isValid(g.url) && isClean(g.url));
                             const validOnly = sortedGroups.filter(g => isValid(g.url));
                             
                             const allInvalid = data.urlGroups.length > 0 && data.urlGroups.every((g: any) => {
                               const res = urlValidationResults[g.url];
                               return res && res !== 'loading' && !(res as any).isReachable;
                             });

                             const suggestedUrl = allInvalid ? null : (validAndClean.length > 0 ? validAndClean[0] : (validOnly.length > 0 ? validOnly[0] : sortedGroups[0]))?.url;

                             return data.urlGroups.map((group: any, index: number) => {
                               const groupLocationIds: string[] = (group.companyLocations ?? []).map((cl: any) => cl.id);
                               const parentSelected = selectedRows.some((r) => r.id === data.id);
                               const groupChecked =
                                 parentSelected &&
                                 groupLocationIds.some((id) => !selectedCompanyLocations.includes(id));

                               const handleGroupCheckboxChange = (checked: boolean) => {
                                 if (checked) {
                                   if (!parentSelected) {
                                     toggleRow(data, true);
                                     const allOtherLocationIds = (data.urlGroups as any[])
                                       .filter((g: any) => g.url !== group.url)
                                       .flatMap((g: any) => (g.companyLocations ?? []).map((cl: any) => cl.id));
                                     setSelectedCompanyLocations((prev) => [...new Set([...prev, ...allOtherLocationIds])]);
                                   } else {
                                     setSelectedCompanyLocations((prev) => 
                                       prev.filter(id => !groupLocationIds.includes(id))
                                     );
                                   }
                                 } else {
                                   setSelectedCompanyLocations((prev) => 
                                     [...new Set([...prev, ...groupLocationIds])]
                                   );
                                   
                                   const anyOtherGroupSelected = (data.urlGroups as any[])
                                     .filter((g: any) => g.url !== group.url)
                                     .some((g: any) => 
                                       (g.companyLocations ?? []).some((cl: any) => !selectedCompanyLocations.includes(cl.id))
                                     );
                                   
                                   if (!anyOtherGroupSelected && parentSelected) {
                                     toggleRow(data);
                                   }
                                 }
                               };

                               return (
                                 <div
                                   key={group.url + index}
                                   draggable
                                   onDragStart={(e) => {
                                     e.stopPropagation();
                                     handleUrlGroupDragStart(data, group);
                                     e.dataTransfer.effectAllowed = 'move';
                                   }}
                                   className={`relative border rounded-md p-2 transition-all cursor-grab active:cursor-grabbing ${
                                     groupChecked
                                       ? "border-blue-500 bg-blue-50/30 dark:bg-blue-900/10 shadow-sm"
                                       : "border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800"
                                   }`}
                                 >
                                   {/* Suggested Badge */}
                                   {data.urlGroups.length > 1 && group.url === suggestedUrl && (
                                     <span className="absolute -top-2 right-12 px-2 bg-white dark:bg-gray-800 text-[10px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400 z-10">
                                       Suggested
                                     </span>
                                   )}
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center gap-2 min-w-0 w-[320px] shrink-0">
                                    <input
                                      type="checkbox"
                                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:border-gray-100 shrink-0"
                                      checked={groupChecked}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        handleGroupCheckboxChange(e.target.checked);
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    {data.linkedIn && (
                                      <a
                                        href={data.linkedIn.startsWith("http") ? data.linkedIn : `https://${data.linkedIn}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="hover:text-blue-500 shrink-0"
                                      >
                                        <LinkedIn width="1rem" />
                                      </a>
                                    )}
                                    <div className="min-w-0 truncate">
                                      {group.url ? (
                                        <a
                                          href={group.url.startsWith("http") ? group.url : `https://${group.url}`}
                                          target="_blank"
                                          rel="noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          title={group.url}
                                          className={`text-xs hover:underline truncate ${group.flag ? 'text-red-500' : 'text-entntblue'}`}
                                        >
                                          {group.url}
                                        </a>
                                      ) : (
                                        <span className="text-xs text-gray-400 italic">(no url)</span>
                                      )}
                                      {(group.name || data.name) && (
                                        <p className="text-[10px] text-gray-500 truncate" title={group.name || data.name}>
                                          {group.name || data.name}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap gap-1 flex-1">
                                    {(group.companyLocations ?? []).map((cl: any) => (
                                      <span
                                        key={cl.id}
                                        className={`${
                                          groupChecked && selectedCompanyLocations.includes(cl.id)
                                            ? "border border-black bg-transparent"
                                            : cl.city?.isConfirmed
                                            ? "bg-red-500 text-white"
                                            : "bg-entntblue text-white"
                                        } cursor-pointer rounded-md py-0.5 px-1 text-xs`}
                                        title={String(cl.city?.count ?? 0)}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!parentSelected) return;
                                          toggleSelectedOptions(cl.id);
                                        }}
                                      >
                                        <span>
                                          {cl.city?.isConfirmed ? (
                                            <span className="pr-1">
                                              {cl.city?.majorCity ? (
                                                <span title={`Sub City Of ${cl.city.majorCity.name}`}>{cl.city.name}</span>
                                              ) : (
                                                <span title={`${cl.city.name} is a major city`}>{cl.city.name}</span>
                                              )}
                                            </span>
                                          ) : (
                                            <span className="pr-1">{cl.city?.name}</span>
                                          )}
                                          {cl.city?.name && (
                                            <span className="border-l border-white px-1">{cl.city?.count ?? 0}</span>
                                          )}
                                        </span>
                                      </span>
                                    ))}
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    {urlValidationResults[group.url] === 'loading' && (
                                      <span className="text-xs text-gray-400">Checking...</span>
                                    )}
                                    {urlValidationResults[group.url] && urlValidationResults[group.url] !== 'loading' && (
                                      <span className={`text-xs font-medium ${(urlValidationResults[group.url] as any).isReachable ? 'text-green-600' : 'text-red-500'}`}>
                                        {(urlValidationResults[group.url] as any).isReachable
                                          ? `✓ Valid URL`
                                          : `✗ Invalid URL`}
                                      </span>
                                    )}
                                    <button
                                      className="inline-flex items-center gap-1 text-xs bg-entntblue text-white hover:bg-blue-700 rounded px-2 py-0.5 whitespace-nowrap"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        setCandidatesModalUrl(group.url);
                                        setCandidatesModalStudents([]);
                                        setCandidatesModalLoading(true);
                                        setCandidatesModalOpen(true);
                                        try {
                                          const furl = group.url.replace(/^(https?:\/\/)?(www\.)?/, "").replace(/\/$/, "");
                                          const candidates = await getCandidatesByCompanyUrl(furl, token);
                                          setCandidatesModalStudents(candidates);
                                        } catch {}
                                        setCandidatesModalLoading(false);
                                      }}
                                    >
                                      <UserGroupIcon className="w-3.5 h-3.5" />
                                      Candidates
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        })()}
                      </div>
                      </div>
                    )}

                    {/* URL Groups for Universities */}
                    {selected === TABS[1] && data.urlGroups && (
                      <div className="w-full">
                        <p className="font-bold mb-2">URL Variants:</p>
                        <div className="flex flex-col gap-2">
                          {data.urlGroups.map((group: any) => {
                            const groupUniIds: string[] = group.universities.map((u: any) => u.id);
                            const parentSelected = selectedRows.some((r) => r.id === data.id);
                            const groupChecked =
                              parentSelected &&
                              groupUniIds.some((id) => !selectedCompanyLocations.includes(id));

                            const handleGroupCheckboxChange = (checked: boolean) => {
                              if (checked) {
                                if (!parentSelected) {
                                  toggleRow(data, true);
                                  const otherGroupUniIds = (data.urlGroups as any[])
                                    .filter((g: any) => g.url !== group.url)
                                    .flatMap((g: any) => (g.universities as any[]).map((u: any) => u.id));
                                  setSelectedCompanyLocations((prev) => [...new Set([...prev, ...otherGroupUniIds])]);
                                } else {
                                  setSelectedCompanyLocations((prev) =>
                                    prev.filter((id) => !groupUniIds.includes(id))
                                  );
                                }
                              } else {
                                setSelectedCompanyLocations((prev) => [...new Set([...prev, ...groupUniIds])]);
                                const otherGroupsAllExcluded = (data.urlGroups as any[])
                                  .filter((g: any) => g.url !== group.url)
                                  .every((g: any) =>
                                    (g.universities as any[]).every((u: any) => selectedCompanyLocations.includes(u.id))
                                  );
                                if (otherGroupsAllExcluded && parentSelected) {
                                  toggleRow(data);
                                }
                              }
                            };

                            return (
                              <div
                                key={group.url}
                                draggable
                                onDragStart={(e) => {
                                  e.stopPropagation();
                                  handleUrlGroupDragStart(data, group);
                                  e.dataTransfer.effectAllowed = 'move';
                                }}
                                className="border border-gray-200 dark:border-gray-600 rounded-md p-2 cursor-grab active:cursor-grabbing"
                              >
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center gap-2 min-w-0 w-[320px] shrink-0">
                                    <input
                                      type="checkbox"
                                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:border-gray-100 shrink-0"
                                      checked={groupChecked}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        handleGroupCheckboxChange(e.target.checked);
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    {data.linkedIn && (
                                      <a
                                        href={data.linkedIn.startsWith("http") ? data.linkedIn : `https://${data.linkedIn}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="hover:text-blue-500 shrink-0"
                                      >
                                        <LinkedIn width="1rem" />
                                      </a>
                                    )}
                                    <div className="min-w-0 truncate">
                                      {group.url ? (
                                        <a
                                          href={group.url.startsWith("http") ? group.url : `https://${group.url}`}
                                          target="_blank"
                                          rel="noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          title={group.url}
                                          className={`text-xs hover:underline truncate ${group.flag ? 'text-red-500' : 'text-entntblue'}`}
                                        >
                                          {group.url}
                                        </a>
                                      ) : (
                                        <span className="text-xs text-gray-400 italic">(no url)</span>
                                      )}
                                      {data.name && (
                                        <p className="text-[10px] text-gray-500 truncate" title={data.name}>
                                          {data.name}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap gap-1 flex-1">
                                    {group.universities.map((uni: any) => (
                                      <span
                                        key={uni.id}
                                        className={`${
                                          groupChecked && selectedCompanyLocations.includes(uni.id)
                                            ? "border border-black bg-transparent"
                                            : uni.city?.isConfirmed
                                            ? "bg-red-500 text-white"
                                            : "bg-entntblue text-white"
                                        } cursor-pointer rounded-md py-0.5 px-1 text-xs`}
                                        title={String(uni.count)}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!parentSelected) return;
                                          toggleSelectedOptions(uni.id);
                                        }}
                                      >
                                        <span>
                                          {uni.city?.isConfirmed ? (
                                            <span className="pr-1">
                                              {uni.city?.majorCity ? (
                                                <span title={`Sub City Of ${uni.city.majorCity.name}`}>{uni.city.name}</span>
                                              ) : (
                                                <span title={`${uni.city.name} is a major city`}>{uni.city.name}</span>
                                              )}
                                            </span>
                                          ) : (
                                            <span className="pr-1">{uni.city?.name}</span>
                                          )}
                                          {uni.city?.name && (
                                            <span className="border-l border-white px-1">{uni.count}</span>
                                          )}
                                        </span>
                                      </span>
                                    ))}
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    {urlValidationResults[group.url] === 'loading' && (
                                      <span className="text-xs text-gray-400">Checking...</span>
                                    )}
                                    {urlValidationResults[group.url] && urlValidationResults[group.url] !== 'loading' && (
                                      <span className={`text-xs font-medium ${(urlValidationResults[group.url] as any).isReachable ? 'text-green-600' : 'text-red-500'}`}>
                                        {(urlValidationResults[group.url] as any).isReachable
                                          ? `✓ Valid URL`
                                          : `✗ Invalid URL`}
                                      </span>
                                    )}
                                    <button
                                      className="inline-flex items-center gap-1 text-xs bg-entntblue text-white hover:bg-blue-700 rounded px-2 py-0.5 whitespace-nowrap"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        setCandidatesModalUrl(group.url);
                                        setCandidatesModalStudents([]);
                                        setCandidatesModalLoading(true);
                                        setCandidatesModalOpen(true);
                                        try {
                                          const candidates = await getCandidatesByUniversityUrl(group.url, token);
                                          setCandidatesModalStudents(candidates);
                                        } catch {}
                                        setCandidatesModalLoading(false);
                                      }}
                                    >
                                      <UserGroupIcon className="w-3.5 h-3.5" />
                                      Candidates
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Candidates inline for Cities tab only (companies/universities use per-URL-group candidates button) */}
                    {selected !== TABS[1] && selected !== TABS[0] && students.length > 0 && (
                      <div className="w-full mt-4">
                        <p className="font-bold">Candidates:</p>
                        <div className="max-h-[20rem] overflow-y-scroll grid grid-cols-1 gap-2 mt-2">
                          {students.map((student: ICandidate) => (
                            <div
                              key={student.id}
                              className="p-2 bg-gray-100 dark:bg-gray-700 rounded-md shadow-md"
                            >
                              <a
                                href={`/candidates/${student.id}`}
                                className="hover:underline"
                                target="_blank"
                                rel="noreferrer"
                              >
                                {student.firstName} {student.lastName}
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </td>
            </tr>
          )}
        </React.Fragment>
      ))}
      {loadingMore && (
        <tr>
          <td colSpan={9} className="py-4">
            <Spinner height="h-8" width="w-8" />
          </td>
        </tr>
      )}
    </tbody>

    {/* Merge University Dialog */}
    {mergeDialog && (
      <Dialog open={true} onClose={() => !isMerging && setMergeDialog(null)} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-darkbglight">
            <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
              {mergeDialog.sourceUrlGroup 
                ? (selected === TABS[0] ? 'Move Company URL Group' : 'Move University URL Group') 
                : (selected === TABS[0] ? 'Merge Companies' : 'Merge Universities')}
            </Dialog.Title>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {mergeDialog.sourceUrlGroup
                ? `Move "${mergeDialog.sourceUrlGroup.url}" (${mergeDialog.sourceUrlGroup.count} candidates) from "${mergeDialog.sourceData.name}" into "${mergeDialog.targetData.name}"`
                : `Merge "${mergeDialog.sourceData.name}" (${mergeDialog.sourceData.count} candidates) into "${mergeDialog.targetData.name}" (${mergeDialog.targetData.count} candidates)`
              }
            </p>

            <div className="mt-4 space-y-3">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Choose the name to keep:</p>

              <label className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer ${
                mergeDialog.nameChoice === 'target' ? 'border-entntblue bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-600'
              }`}>
                <input
                  type="radio"
                  name="mergeName"
                  checked={mergeDialog.nameChoice === 'target'}
                  onChange={() => setMergeDialog(prev => prev ? { ...prev, nameChoice: 'target' } : null)}
                  className="text-entntblue focus:ring-entntblue"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{mergeDialog.targetData.name}</p>
                  <p className="text-xs text-gray-500">Target — {mergeDialog.targetData.count} candidates</p>
                </div>
              </label>

              <label className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer ${
                mergeDialog.nameChoice === 'source' ? 'border-entntblue bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-600'
              }`}>
                <input
                  type="radio"
                  name="mergeName"
                  checked={mergeDialog.nameChoice === 'source'}
                  onChange={() => setMergeDialog(prev => prev ? { ...prev, nameChoice: 'source' } : null)}
                  className="text-entntblue focus:ring-entntblue"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{mergeDialog.sourceData.name}</p>
                  <p className="text-xs text-gray-500">Source — {mergeDialog.sourceData.count} candidates</p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer ${
                mergeDialog.nameChoice === 'custom' ? 'border-entntblue bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-600'
              }`}>
                <input
                  type="radio"
                  name="mergeName"
                  checked={mergeDialog.nameChoice === 'custom'}
                  onChange={() => setMergeDialog(prev => prev ? { ...prev, nameChoice: 'custom' } : null)}
                  className="text-entntblue focus:ring-entntblue mt-0.5"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Custom name</p>
                    {mergeDialog.nameChoice === 'custom' && (
                      <button
                        onClick={handleAiSuggestName}
                        disabled={isAiSuggesting}
                        className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 disabled:opacity-50"
                      >
                        {isAiSuggesting ? (
                          <>
                            <span className="inline-block w-3 h-3 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                            Suggesting...
                          </>
                        ) : (
                          <>AI Suggest</>
                        )}
                      </button>
                    )}
                  </div>
                  {mergeDialog.nameChoice === 'custom' && (
                    <input
                      type="text"
                      autoFocus
                      value={mergeDialog.customName}
                      onChange={(e) => setMergeDialog(prev => prev ? { ...prev, customName: e.target.value } : null)}
                      placeholder="Enter custom name or use AI Suggest..."
                      className="mt-1 w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-entntblue dark:bg-darkbg dark:border-gray-600 dark:text-white"
                    />
                  )}
                </div>
              </label>
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => setMergeDialog(null)}
                disabled={isMerging}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleMergeConfirm}
                disabled={isMerging || (mergeDialog.nameChoice === 'custom' && !mergeDialog.customName.trim())}
                className="rounded-md bg-entntblue px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-entntorange disabled:bg-gray-400"
              >
                {isMerging ? 'Merging...' : 'Confirm Merge'}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    )}

    {/* Candidates Modal */}
    <Transition.Root show={candidatesModalOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setCandidatesModalOpen(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-400 bg-opacity-50 dark:bg-opacity-20 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-darkbglight text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md">
                <div className="px-4 pb-4 pt-5 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                        <UserGroupIcon className="h-5 w-5 text-entntblue" />
                      </div>
                      <div>
                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                          Candidates
                        </Dialog.Title>
                        {candidatesModalUrl && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 break-all">{candidatesModalUrl}</p>
                        )}
                      </div>
                    </div>
                    <button onClick={() => setCandidatesModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {candidatesModalLoading ? (
                      <div className="flex justify-center py-4">
                        <Spinner />
                      </div>
                    ) : candidatesModalStudents.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-4">No candidates found.</p>
                    ) : (
                      <div className="grid grid-cols-1 gap-2">
                        {candidatesModalStudents.map((student: ICandidate) => (
                          <a
                            key={student.id}
                            href={`/candidates/${student.id}`}
                            className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-700 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                          >
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-entntblue text-white text-xs font-medium shrink-0">
                              {student.firstName?.[0]}{student.lastName?.[0]}
                            </div>
                            <span className="text-sm text-gray-900 dark:text-white">
                              {student.firstName} {student.lastName}
                            </span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 sm:px-6">
                  <span className="text-xs text-gray-500">{candidatesModalStudents.length} candidate{candidatesModalStudents.length !== 1 ? 's' : ''}</span>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
    </>
  );
};

export default DataGrid;